import type { DragEndEvent } from '@dnd-kit/core';
import type { IStackProps } from '@sixui/ui';
import { useCallback, useEffect, useState } from 'react';
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createSequence, filterUndefineds } from '@olivierpascal/helpers';
import { Button, Snackbar, Stack } from '@sixui/ui';
import { buttonTokens } from '@sixui/ui/components/Button/Button.stylex';
import { densityTokens } from '@sixui/ui/themes/base/density.stylex';
import { scaleTokens } from '@sixui/ui/themes/base/scale.stylex';
import { spacingTokens } from '@sixui/ui/themes/base/spacing.stylex';
import stylex from '@stylexjs/stylex';
import { Trans, useTranslation } from 'react-i18next';

import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { HorizontalCard } from '~/components/molecules/HorizontalCard';
import { useAppPreviewDispatch } from '~/components/organisms/AppPreview';
import {
  CategoryListItem,
  CategoryListItemSkeleton,
} from '~/components/organisms/CategoryListItem';
import { hashKey } from '~/helpers/hashKey';
import { findPath } from '~/helpers/tree';
import { ApiError, useApi } from '~/lib/api';
import { useCategoriesQuery } from '~/lib/api/modules/Categories/hooks/useCategoriesQuery';

const SNACKBAR_AUTO_HIDE_DURATION = 3000;
const DEFAULT_SKELETON_COUNT = 3;
const MAX_DEPTH = 2;
const MIN_DENSITY = -2;
const MAX_DENSITY = 0;
const DENSITY = `${densityTokens.interval} * clamp(${MIN_DENSITY}, ${densityTokens.density}, ${MAX_DENSITY}) * ${scaleTokens.scale}`;

export type ICategoryListProps = IStackProps & {
  appId: string;
  parentId?: string;
};

const styles = stylex.create({
  grid: {
    display: 'grid',
    columnGap: spacingTokens.padding$4,
    rowGap: `calc(${spacingTokens.padding$4} + ${DENSITY})`,
  },
  grid$sm: {
    gridTemplateColumns: `calc(400px + ${spacingTokens.padding$4})`,
  },
  grid$md: {
    gridTemplateColumns: 'repeat(2, 200px)',
  },
  warning: {
    padding: buttonTokens.leadingIconLeadingSpace,
  },
  titleContainer: {
    marginLeft: 12,
    marginRight: 'calc(40px + 6px + 6px)',
  },
});

export const CategoryList: React.FC<ICategoryListProps> = (props) => {
  const { sx, appId, parentId } = props;
  const { t } = useTranslation();

  // We use an optimistic delete strategy to avoid waiting for the server to
  // respond before updating the UI. In order to do this, we need to keep track
  // of the categories pending deletion, so we can filter them out from the
  // list of categories and restore them in case of failure.
  const [categoryIdsPendingDeletion, setCategoryIdsPendingDeletion] = useState<
    Array<string>
  >([]);

  const api = useApi();
  const appPreviewDispatch = useAppPreviewDispatch();
  const categoriesQuery = useCategoriesQuery({ appId });
  const [snackbarOpened, setSnackbarOpened] = useState(false);
  const [snackbarError, setSnackbarError] = useState<string>();

  const childCategories = categoriesQuery.data?.categories.filter(
    (category) =>
      !categoryIdsPendingDeletion.includes(category._id) &&
      ((parentId === undefined && !category.parentId) ||
        category.parentId === parentId),
  );

  const path =
    categoriesQuery.data?.categories && parentId
      ? findPath(categoriesQuery.data.categories, parentId)
      : undefined;
  const depth = path?.length ?? 0;
  const canCreateChild = depth < MAX_DEPTH;

  const [categoryIds, setCategoryIds] = useState<Array<string>>([]);

  const dense = childCategories && childCategories.length > 8;

  // Create a key to trigger a re-render when categories change, independently
  // of their order.
  const categoriesHashKey = childCategories
    ? hashKey(
        childCategories.map((category) => category._id),
        { sortKeys: true },
      )
    : undefined;

  useEffect(
    () =>
      setCategoryIds(childCategories?.map((category) => category._id) ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categoriesHashKey],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const movedCategory = childCategories?.find(
        (category) => category._id === active.id,
      );
      if (!movedCategory) {
        return;
      }

      const oldIndex = categoryIds.indexOf(active.id as string);
      const newIndex = categoryIds.indexOf(over.id as string);
      if (oldIndex < 0 || newIndex < 0) {
        return;
      }

      // We use optimistic updates to avoid waiting for the server to respond
      // before updating the UI. In order to do this, we need to save the
      // current categories order before updating them.
      const previousCategoryIds = categoryIds.slice();
      setCategoryIds(arrayMove(categoryIds, oldIndex, newIndex));

      void api.categories
        .updateOrder(appId, movedCategory._id, {
          order: newIndex + 1,
        })
        .then((response) => {
          if (!response) {
            throw new Error('Invalid API response');
          }

          appPreviewDispatch.navigateToRoute({
            appId,
            path: parentId ? `/categories/${parentId}` : '/',
            tab: 'categories',
          });
        })
        .catch(() => {
          // Restore the previous categories order in case of failure.
          setCategoryIds(previousCategoryIds);
        });
    },
    [api, appId, categoryIds, childCategories, appPreviewDispatch, parentId],
  );

  const handleDeleteStart = (categoryId: string): void =>
    setCategoryIdsPendingDeletion((previous) => [...previous, categoryId]);

  const handleDeleteError = (categoryId: string, error: unknown): void => {
    setCategoryIdsPendingDeletion((previous) =>
      previous.filter((id) => id !== categoryId),
    );

    if (error instanceof ApiError) {
      setSnackbarOpened(true);
      setSnackbarError(t('CategoryList.errors.cannotDeleteCategory'));

      if (
        error.errors
          .map((error) => error.code)
          .includes('category_has_children_categories')
      ) {
        setSnackbarError(
          t('CategoryList.errors.cannotDeleteCategoryWithChildren'),
        );
      }
    }
  };

  const orderedCategories = filterUndefineds(
    categoryIds.map((categoryId) =>
      childCategories?.find((category) => category._id === categoryId),
    ),
  );

  return (
    // A container div is needed because DndContext create sibling elements.
    <Stack sx={sx} gap={2}>
      <Snackbar
        open={snackbarOpened}
        onClose={() => setSnackbarOpened(false)}
        autoHideDuration={SNACKBAR_AUTO_HIDE_DURATION}
      >
        {snackbarError}
      </Snackbar>

      <Stack horizontal gap={2}>
        <Button
          variant="text"
          icon={<FontAwesomeIcon icon={faPlus} />}
          href={
            parentId
              ? `/apps/${appId}/categories/${parentId}/create`
              : `/apps/${appId}/categories/create`
          }
          disabled={!canCreateChild}
        >
          {t('CategoryList.create-category')}
        </Button>

        <Button
          variant="text"
          icon={<FontAwesomeIcon icon={faPlus} />}
          href={
            parentId
              ? `/apps/${appId}/links/${parentId}/create`
              : `/apps/${appId}/links/create`
          }
          disabled={!canCreateChild}
        >
          {t('CategoryList.create-link')}
        </Button>
      </Stack>

      {categoriesQuery.isError ? (
        <ErrorMessage error={categoriesQuery.error} />
      ) : (
        <div
          {...stylex.props(
            styles.grid,
            dense ? styles.grid$md : styles.grid$sm,
          )}
        >
          {(!categoriesQuery.isLoading && canCreateChild) ||
          categoriesQuery.isLoading ? (
            !categoriesQuery.isLoading && !childCategories?.length ? (
              <HorizontalCard
                title={
                  <div {...stylex.props(styles.titleContainer)}>
                    {parentId
                      ? t('CategoryList.create-category')
                      : t('CategoryList.create-your-first-category')}
                  </div>
                }
                size={dense ? 'sm' : 'md'}
                href={
                  parentId
                    ? `/apps/${appId}/categories/${parentId}/create`
                    : `/apps/${appId}/categories/create`
                }
                placeholder
              />
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={categoryIds}>
                  {categoriesQuery.isLoading
                    ? createSequence(DEFAULT_SKELETON_COUNT).map((index) => (
                        <CategoryListItemSkeleton
                          key={index}
                          size={dense ? 'sm' : 'md'}
                        />
                      ))
                    : categoriesQuery.data
                      ? orderedCategories?.map((category) => (
                          <CategoryListItem
                            key={category._id}
                            category={category}
                            size={dense ? 'sm' : 'md'}
                            onDeleteStart={() =>
                              handleDeleteStart(category._id)
                            }
                            onDeleteError={(error) =>
                              handleDeleteError(category._id, error)
                            }
                          />
                        ))
                      : null}
                </SortableContext>
              </DndContext>
            )
          ) : (
            <div {...stylex.props(styles.warning)}>
              <Trans
                i18nKey="CategoryList.errors.maxDepthReached"
                values={{ maxDepth: MAX_DEPTH }}
              />
            </div>
          )}
        </div>
      )}
    </Stack>
  );
};
